import React, { useState } from 'react';
import {
  MessageSquare,
  Bot,
  Phone,
  Users,
  Radio,
  Briefcase,
  ShoppingBag,
  FolderLock,
  Calendar as CalendarIcon,
  CheckSquare,
  MapPin,
  Music,
  ShieldCheck,
  Sparkles,
  Send,
  Search,
  Plus,
  ArrowRight,
  Flame,
  Star,
  Zap,
  Clock,
  Mic,
  Video,
  Eye,
  Wand2,
  FileText,
  Volume2,
  Copy,
  Check,
  ExternalLink,
  ChevronRight,
  RefreshCw,
  Bell,
  Lock,
  Compass,
  Award,
  ShoppingCart,
  TrendingUp,
  Sliders,
  Filter,
} from 'lucide-react';
import { UserProfile, Conversation } from '../types';
import { soundEffects } from '../lib/soundEffects';
import { WateenHubAnalytics } from './WateenHubAnalytics';

interface WateenHubViewProps {
  currentUser: UserProfile;
  conversations: Conversation[];
  onOpenChat: (conversationId: string) => void;
  onOpenAIModal: () => void;
  onOpenStartCall: () => void;
  onOpenCreateChat: () => void;
  onOpenPremium: () => void;
  onNavigateTab: (tab: any) => void;
}

export type HubModule =
  | 'overview'
  | 'analytics'
  | 'messages'
  | 'ai'
  | 'calls'
  | 'communities'
  | 'channels'
  | 'business'
  | 'store'
  | 'files'
  | 'calendar'
  | 'tasks'
  | 'location'
  | 'media'
  | 'privacy';

export const WateenHubView: React.FC<WateenHubViewProps> = ({
  currentUser,
  conversations,
  onOpenChat,
  onOpenAIModal,
  onOpenStartCall,
  onOpenCreateChat,
  onOpenPremium,
  onNavigateTab,
}) => {
  const [activeModule, setActiveModule] = useState<HubModule>('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedText, setCopiedText] = useState<string | null>(null);

  // Quick AI interactive state
  const [aiQuickInput, setAiQuickInput] = useState('');
  const [aiQuickTone, setAiQuickTone] = useState<'polite' | 'casual' | 'formal' | 'promo' | 'apology'>('formal');
  const [aiQuickResult, setAiQuickResult] = useState('');
  const [aiQuickLoading, setAiQuickLoading] = useState(false);

  // Tasks module state
  const [tasks, setTasks] = useState([
    { id: '1', title: 'مراجعة مسودة العقد مع المستشار', done: false, priority: 'high', time: '14:30' },
    { id: '2', title: 'إرسال التقرير الأسبوعي لفريق وتين', done: true, priority: 'medium', time: '11:00' },
    { id: '3', title: 'اجتماع تنسيق إطلاق المتجر الرقمي', done: false, priority: 'high', time: '17:00' },
    { id: '4', title: 'تأكيد حجز تذاكر السفر وتلخيص المواعيد', done: false, priority: 'low', time: '20:00' },
  ]);
  const [newTaskTitle, setNewTaskTitle] = useState('');

  // Store cart state
  const [cartCount, setCartCount] = useState(2);
  const [storeItems, setStoreItems] = useState([
    {
      id: 'p1',
      name: 'سماعات وتين Pro اللاسلكية ذات العزل الذكي',
      price: '349 ر.س',
      category: 'إلكترونيات',
      rating: 4.9,
      image: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=400&auto=format&fit=crop&q=80',
      badge: 'الأكثر طلباً',
      verified: true,
    },
    {
      id: 'p2',
      name: 'اشتراك وتين للأعمال السنوي (Business Suite)',
      price: '899 ر.س',
      category: 'خدمات سحابية',
      rating: 5.0,
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&auto=format&fit=crop&q=80',
      badge: 'موثق رسمياً',
      verified: true,
    },
    {
      id: 'p3',
      name: 'حقيبة أعمال جلدية فاخرة مع حماية RFID',
      price: '275 ر.س',
      category: 'أزياء واكسسوارات',
      rating: 4.8,
      image: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=400&auto=format&fit=crop&q=80',
      badge: 'خصم 20%',
      verified: true,
    },
  ]);

  // Calendar events
  const [calendarEvents, setCalendarEvents] = useState([
    { id: 'ev1', title: 'جلسة عصف ذهني لميزات وتين 2026', time: '13:00 - 14:00', date: 'اليوم', type: 'call' },
    { id: 'ev2', title: 'موعد استخراج نتائج الفحص الطبي', time: '16:30', date: 'اليوم', type: 'reminder' },
    { id: 'ev3', title: 'بث مباشر في قناة وتين تك الرسمية', time: '21:00', date: 'غداً', type: 'channel' },
  ]);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedText(text);
    soundEffects.playSent();
    setTimeout(() => setCopiedText(null), 2000);
  };

  const handleGenerateQuickAI = () => {
    if (!aiQuickInput.trim()) return;
    setAiQuickLoading(true);
    soundEffects.playPop();

    setTimeout(() => {
      let output = '';
      if (aiQuickTone === 'formal') {
        output = `تحية طيبة وبعد، بالإشارة إلى الموضوع المطروح: "${aiQuickInput}"، نود إحاطتكم علماً بأننا نسعى لتقديم أعلى درجات الجودة والالتزام في كافة تفاصيل العمل. شاكرين ومقدرين حسن تعاونكم الدائم.`;
      } else if (aiQuickTone === 'casual') {
        output = `أهلاً يا غالي! بخصوص موضوع "${aiQuickInput}"، كل شيء تمام وجاهز مثل ما اتفقنا وأبشر بالخير في أي وقت! ✨`;
      } else if (aiQuickTone === 'apology') {
        output = `أعتذر بصدق عن أي إزعاج أو تأخير بخصوص "${aiQuickInput}". أقدّر وقتكم وتفهمكم، وسأقوم بمتابعة الأمر شخصياً لضمان معالجته فوراً.`;
      } else if (aiQuickTone === 'promo') {
        output = `🔥 عرض حصري لعملاء وتين! اكتشف الآن تفاصيل "${aiQuickInput}" واستمتع بتجربة رقمية فريدة تليق بتطلعاتك. تواصل معنا لمعرفة المزيد!`;
      } else {
        output = `السلام عليكم ورحمة الله وبركاته، أرجو أن تكون بخير وعافية. بخصوص "${aiQuickInput}"، يسعدني مناقشة التفاصيل معكم في الوقت الذي يناسبكم. مع أطيب التحيات.`;
      }
      setAiQuickResult(output);
      setAiQuickLoading(false);
      soundEffects.playReceived();
    }, 600);
  };

  const handleAddTask = () => {
    if (!newTaskTitle.trim()) return;
    const newTask = {
      id: String(Date.now()),
      title: newTaskTitle.trim(),
      done: false,
      priority: 'medium',
      time: 'اليوم',
    };
    setTasks([newTask, ...tasks]);
    setNewTaskTitle('');
    soundEffects.playSent();
  };

  const toggleTask = (id: string) => {
    setTasks(tasks.map((t) => (t.id === id ? { ...t, done: !t.done } : t)));
    soundEffects.playPop();
  };

  const modulesList: Array<{
    id: HubModule;
    label: string;
    icon: any;
    desc: string;
    color: string;
    badge?: string;
  }> = [
    {
      id: 'analytics',
      label: '📊 التحليلات والإحصائيات',
      icon: TrendingUp,
      desc: 'لوحة تفاعلية بـ Recharts: نشاط الرسائل، الوقت المستغرق، وتوزيع أنواع الملفات',
      color: 'from-emerald-500/20 to-teal-500/10 border-emerald-500/30 text-emerald-400',
      badge: 'Recharts 📊',
    },
    {
      id: 'messages',
      label: '💬 الرسائل والمحادثات',
      icon: MessageSquare,
      desc: 'رسائل فورية، محادثات سرية، رسائل مجدولة، وفلاتر وسائط',
      color: 'from-emerald-500/20 to-teal-500/10 border-emerald-500/30 text-emerald-400',
      badge: `${conversations.length} نشطة`,
    },
    {
      id: 'ai',
      label: '🤖 وتين AI الخارق',
      icon: Bot,
      desc: 'مساعد شخصي، كاتب رسائل، تلخيص، شخصيات، وذاكرة ذكية',
      color: 'from-indigo-500/20 to-purple-500/10 border-indigo-500/30 text-indigo-400',
      badge: '50+ ميزة',
    },
    {
      id: 'calls',
      label: '📞 المكالمات والغرف',
      icon: Phone,
      desc: 'صوت، فيديو، غرف صوتية، مشاركة الشاشة، وترجمة مباشرة',
      color: 'from-sky-500/20 to-blue-500/10 border-sky-500/30 text-sky-400',
      badge: 'HD مباشر',
    },
    {
      id: 'communities',
      label: '👥 المجتمعات والمجموعات',
      icon: Users,
      desc: 'مجموعات ضخمة، بوتات ترحيب، واستطلاعات رأي متقدمة',
      color: 'from-violet-500/20 to-purple-500/10 border-violet-500/30 text-violet-400',
      badge: 'تفاعل حي',
    },
    {
      id: 'channels',
      label: '📢 القنوات والبث',
      icon: Radio,
      desc: 'قنوات عامة وخاصة، بثوث، وإشعارات موثقة فورية',
      color: 'from-pink-500/20 to-rose-500/10 border-pink-500/30 text-pink-400',
      badge: 'موثقة',
    },
    {
      id: 'business',
      label: '🏢 وتين للأعمال',
      icon: Briefcase,
      desc: 'حساب تجاري، كتالوج منتجات، رد آلي AI، وCRM للعملاء',
      color: 'from-amber-500/20 to-yellow-500/10 border-amber-500/30 text-amber-400',
      badge: 'Pro Suite',
    },
    {
      id: 'store',
      label: '🛒 سوق وتين الرقمي',
      icon: ShoppingBag,
      desc: 'تسوق منتجات، عروض وتخفيضات، سلة، وتتبع طلبات فوري',
      color: 'from-orange-500/20 to-amber-500/10 border-orange-500/30 text-orange-400',
      badge: 'متجر متكامل',
    },
    {
      id: 'files',
      label: '📁 الملفات والخزنة',
      icon: FolderLock,
      desc: 'خزنة كبسولة زمنية، مستندات مشفرة، وتصنيف سحابي',
      color: 'from-cyan-500/20 to-teal-500/10 border-cyan-500/30 text-cyan-400',
      badge: 'مشفر E2EE',
    },
    {
      id: 'calendar',
      label: '📅 التقويم والمواعيد',
      icon: CalendarIcon,
      desc: 'استخراج المواعيد من الرسائل تلقائياً وتنظيم المواعيد',
      color: 'from-emerald-500/20 to-green-500/10 border-emerald-500/30 text-emerald-400',
      badge: 'ذكاء زمني',
    },
    {
      id: 'tasks',
      label: '✅ المهام والإنتاجية',
      icon: CheckSquare,
      desc: 'قوائم مهام To-Do، تحويل الرسائل لمهام، وتذكيرات صوتية',
      color: 'from-lime-500/20 to-emerald-500/10 border-lime-500/30 text-lime-400',
      badge: `${tasks.filter((t) => !t.done).length} متبقية`,
    },
    {
      id: 'location',
      label: '🗺️ الموقع والمشاركة',
      icon: MapPin,
      desc: 'مشاركة الموقع المباشر Live، نقاط اللقاء، والأماكن المفضلة',
      color: 'from-teal-500/20 to-emerald-500/10 border-teal-500/30 text-teal-400',
      badge: 'خرائط حية',
    },
    {
      id: 'media',
      label: '🎵 استوديو الوسائط',
      icon: Music,
      desc: 'محرر صور وفيديو، عزل خلفيات، تحسين جودة، وتحويل الصوت',
      color: 'from-fuchsia-500/20 to-pink-500/10 border-fuchsia-500/30 text-fuchsia-400',
      badge: 'استوديو AI',
    },
    {
      id: 'privacy',
      label: '🔐 الأمان والخصوصية',
      icon: ShieldCheck,
      desc: 'قفل PIN وFace ID، إدارة الأجهزة، وكشف الروابط المشبوهة',
      color: 'from-rose-500/20 to-red-500/10 border-rose-500/30 text-rose-400',
      badge: 'أعلى حماية',
    },
  ];

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden bg-[#070b12] text-slate-200 font-['Cairo']" dir="rtl">
      {/* 1. TOP HEADER OF WATEEN HUB */}
      <div className="shrink-0 bg-[#0a0f1a]/95 backdrop-blur-xl border-b border-slate-800/80 px-4 sm:px-6 py-3.5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-emerald-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-black text-white flex items-center gap-2">
                <span>مركز وتين الشامل — Wateen Hub</span>
                <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 text-[10px] font-bold">
                  منظومة موحدة 🔥
                </span>
              </h1>
              <p className="text-xs text-slate-400">
                كل ما تحتاجه في مكان واحد: تواصل، ذكاء اصطناعي، أعمال، متجر، وسائط، وإنتاجية بدون مغادرة التطبيق
              </p>
            </div>
          </div>
        </div>

        {/* Search & Actions */}
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64">
            <Search className="w-3.5 h-3.5 absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              placeholder="ابحث في وتين Hub..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-[#111726] border border-slate-800 rounded-xl pr-9 pl-3 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500/50 transition"
            />
          </div>

          <button
            onClick={() => setActiveModule('overview')}
            className={`px-3 py-1.5 rounded-xl border text-xs font-bold transition flex items-center gap-1.5 shrink-0 ${
              activeModule === 'overview'
                ? 'bg-emerald-500 text-white border-emerald-400 shadow-md shadow-emerald-500/20'
                : 'bg-[#121927] border-slate-800 text-slate-300 hover:text-white'
            }`}
          >
            <Compass className="w-3.5 h-3.5" />
            <span>نظرة عامة</span>
          </button>
        </div>
      </div>

      {/* 2. SUB-NAVIGATION PILLS (HORIZONTAL SCROLL) */}
      <div className="shrink-0 bg-[#080d16] border-b border-slate-800/60 px-4 sm:px-6 py-2 overflow-x-auto no-scrollbar flex items-center gap-2">
        <button
          onClick={() => setActiveModule('overview')}
          className={`px-3 py-1.5 rounded-full text-xs font-bold transition shrink-0 flex items-center gap-1.5 ${
            activeModule === 'overview'
              ? 'bg-emerald-500 text-white shadow-sm shadow-emerald-500/30'
              : 'bg-slate-900/80 text-slate-400 hover:text-slate-200 border border-slate-800'
          }`}
        >
          <Sparkles className="w-3.5 h-3.5" />
          <span>الكل (Hub)</span>
        </button>

        {modulesList.map((m) => {
          const Icon = m.icon;
          const isActive = activeModule === m.id;
          return (
            <button
              key={m.id}
              onClick={() => {
                setActiveModule(m.id);
                soundEffects.playPop();
              }}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition shrink-0 flex items-center gap-1.5 ${
                isActive
                  ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-md shadow-emerald-500/20 border border-emerald-400/40'
                  : 'bg-[#0e1422] text-slate-400 hover:text-slate-200 border border-slate-800/80 hover:border-slate-700'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              <span>{m.label.split(' ').slice(1).join(' ') || m.label}</span>
              {m.badge && (
                <span className="px-1.5 py-0.2 rounded-full bg-slate-800/90 text-[10px] text-slate-300 font-mono">
                  {m.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* 3. MAIN CONTENT AREA */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
        {/* OVERVIEW / DASHBOARD VIEW */}
        {activeModule === 'overview' && (
          <>
            {/* Quick AI Briefing Widget ("ماذا فاتني أثناء غيابي؟") */}
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-indigo-950/40 via-[#0e1424] to-purple-950/30 border border-indigo-500/30 p-5 sm:p-6 shadow-2xl">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="space-y-1.5">
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-indigo-500/20 border border-indigo-500/30 text-indigo-300 text-xs font-bold">
                    <Sparkles className="w-3.5 h-3.5" />
                    <span>ملخص الذكاء الاصطناعي اليومي</span>
                  </div>
                  <h2 className="text-lg sm:text-xl font-bold text-white">
                    مرحباً {currentUser?.displayName || currentUser?.username || 'مستخدم وتين'}! ماذا فاتك في وتين اليوم؟
                  </h2>
                  <p className="text-xs text-slate-300 max-w-2xl leading-relaxed">
                    لديك 3 رسائل مهمة تتطلب رداً، موعد اجتماع بعد الظهر، و4 تحديثات جديدة في القنوات التي تتابعها. قام مساعد وتين AI بفرز الأهم أولاً لتوفير وقتك.
                  </p>
                </div>

                <div className="flex items-center gap-2.5 shrink-0">
                  <button
                    onClick={onOpenAIModal}
                    className="px-4 py-2.5 rounded-2xl bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-400 hover:to-purple-500 text-white font-bold text-xs shadow-lg shadow-indigo-500/25 flex items-center gap-2 transition active:scale-95"
                  >
                    <Bot className="w-4 h-4" />
                    <span>فتح وتين AI الكامل</span>
                  </button>
                  <button
                    onClick={() => onNavigateTab('chats')}
                    className="px-4 py-2.5 rounded-2xl bg-slate-800/80 hover:bg-slate-800 text-slate-200 border border-slate-700/60 font-semibold text-xs transition"
                  >
                    <span>عرض المحادثات</span>
                  </button>
                </div>
              </div>

              {/* Instant Smart Actions */}
              <div className="mt-5 pt-4 border-t border-slate-800/60 grid grid-cols-2 sm:grid-cols-5 gap-2.5">
                <button
                  onClick={() => setActiveModule('ai')}
                  className="p-3 rounded-2xl bg-[#111728]/80 hover:bg-[#161e33] border border-slate-800 hover:border-indigo-500/40 text-right transition group"
                >
                  <div className="text-indigo-400 text-xs font-bold mb-1 flex items-center justify-between">
                    <span>صياغة رسالة رسمية</span>
                    <ArrowRight className="w-3 h-3 group-hover:-translate-x-0.5 transition" />
                  </div>
                  <div className="text-[11px] text-slate-400">كاتب AI بالنيابة عنك</div>
                </button>

                <button
                  onClick={() => setActiveModule('analytics')}
                  className="p-3 rounded-2xl bg-[#111728]/80 hover:bg-[#161e33] border border-slate-800 hover:border-emerald-500/40 text-right transition group"
                >
                  <div className="text-emerald-400 text-xs font-bold mb-1 flex items-center justify-between">
                    <span>لوحة الإحصائيات 📊</span>
                    <ArrowRight className="w-3 h-3 group-hover:-translate-x-0.5 transition" />
                  </div>
                  <div className="text-[11px] text-slate-400">نشاطك اليومي بالـ Recharts</div>
                </button>

                <button
                  onClick={() => setActiveModule('tasks')}
                  className="p-3 rounded-2xl bg-[#111728]/80 hover:bg-[#161e33] border border-slate-800 hover:border-emerald-500/40 text-right transition group"
                >
                  <div className="text-emerald-400 text-xs font-bold mb-1 flex items-center justify-between">
                    <span>استخراج المهام</span>
                    <ArrowRight className="w-3 h-3 group-hover:-translate-x-0.5 transition" />
                  </div>
                  <div className="text-[11px] text-slate-400">تحويل المحادثة إلى مهام</div>
                </button>

                <button
                  onClick={() => setActiveModule('business')}
                  className="p-3 rounded-2xl bg-[#111728]/80 hover:bg-[#161e33] border border-slate-800 hover:border-amber-500/40 text-right transition group"
                >
                  <div className="text-amber-400 text-xs font-bold mb-1 flex items-center justify-between">
                    <span>وتين للأعمال</span>
                    <ArrowRight className="w-3 h-3 group-hover:-translate-x-0.5 transition" />
                  </div>
                  <div className="text-[11px] text-slate-400">الكتالوج والرد التلقائي</div>
                </button>

                <button
                  onClick={() => setActiveModule('store')}
                  className="p-3 rounded-2xl bg-[#111728]/80 hover:bg-[#161e33] border border-slate-800 hover:border-orange-500/40 text-right transition group"
                >
                  <div className="text-orange-400 text-xs font-bold mb-1 flex items-center justify-between">
                    <span>سوق وتين</span>
                    <ArrowRight className="w-3 h-3 group-hover:-translate-x-0.5 transition" />
                  </div>
                  <div className="text-[11px] text-slate-400">تسوق وتتبع الطلبات</div>
                </button>
              </div>
            </div>

            {/* DATA VISUALIZATION DASHBOARD (RECHARTS) */}
            <WateenHubAnalytics currentUserDisplayName={currentUser.displayName} />

            {/* 13 MODULES MASTER GRID */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base font-bold text-white flex items-center gap-2">
                    <span>أقسام منظومة وتين الشاملة</span>
                    <span className="text-xs text-slate-400 font-normal">(13 مركزاً رقمياً مدمجاً)</span>
                  </h3>
                  <p className="text-xs text-slate-400">اضغط على أي قسم للدخول الفوري وتجربة الميزات التفاعلية</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {modulesList.map((mod) => {
                  const Icon = mod.icon;
                  return (
                    <div
                      key={mod.id}
                      onClick={() => {
                        setActiveModule(mod.id);
                        soundEffects.playPop();
                      }}
                      className="group cursor-pointer rounded-3xl bg-[#0d1322] hover:bg-[#121a2e] border border-slate-800/80 hover:border-emerald-500/40 p-5 transition-all duration-200 hover:shadow-xl hover:shadow-emerald-500/5 flex flex-col justify-between"
                    >
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <div
                            className={`w-11 h-11 rounded-2xl bg-gradient-to-tr ${mod.color} flex items-center justify-center border shadow-inner`}
                          >
                            <Icon className="w-5 h-5" />
                          </div>
                          {mod.badge && (
                            <span className="px-2.5 py-0.5 rounded-full bg-slate-800 border border-slate-700/60 text-slate-300 text-[11px] font-semibold">
                              {mod.badge}
                            </span>
                          )}
                        </div>

                        <h4 className="text-sm font-bold text-white group-hover:text-emerald-400 transition mb-1">
                          {mod.label}
                        </h4>
                        <p className="text-xs text-slate-400 leading-relaxed">{mod.desc}</p>
                      </div>

                      <div className="mt-4 pt-3 border-t border-slate-800/50 flex items-center justify-between text-xs text-slate-400 group-hover:text-emerald-400 font-semibold">
                        <span>فتح القسم</span>
                        <ChevronRight className="w-4 h-4 group-hover:-translate-x-1 transition" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* MODULE: ANALYTICS & DATA VISUALIZATION (RECHARTS) */}
        {activeModule === 'analytics' && (
          <div className="space-y-6">
            <WateenHubAnalytics currentUserDisplayName={currentUser.displayName} />
          </div>
        )}

        {/* 1. MODULE: MESSAGES */}
        {activeModule === 'messages' && (
          <div className="space-y-6">
            <div className="rounded-3xl bg-[#0d1322] border border-slate-800 p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                  <h3 className="text-base font-bold text-white flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-emerald-400" />
                    <span>إدارة المراسلة الذكية وفلاتر المحادثات</span>
                  </h3>
                  <p className="text-xs text-slate-400">
                    تحكم بالرسائل النصية والصوتية، الرسائل المجدولة، المحادثات السرية، وتصفية الوسائط.
                  </p>
                </div>
                <button
                  onClick={() => onNavigateTab('chats')}
                  className="px-4 py-2 rounded-2xl bg-emerald-500 hover:bg-emerald-400 text-white text-xs font-bold flex items-center gap-1.5 shadow-lg shadow-emerald-500/20"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  <span>الانتقال لشاشة المحادثات</span>
                </button>
              </div>

              {/* Quick Messaging Filters */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <div className="p-3.5 rounded-2xl bg-[#111728] border border-slate-800 text-center">
                  <div className="text-lg font-bold text-white mb-0.5">{conversations.length}</div>
                  <div className="text-xs text-slate-400">إجمالي المحادثات</div>
                </div>
                <div className="p-3.5 rounded-2xl bg-[#111728] border border-slate-800 text-center">
                  <div className="text-lg font-bold text-emerald-400 mb-0.5">E2EE</div>
                  <div className="text-xs text-slate-400">تشفير تام من طرف لطرف</div>
                </div>
                <div className="p-3.5 rounded-2xl bg-[#111728] border border-slate-800 text-center">
                  <div className="text-lg font-bold text-indigo-400 mb-0.5">وتين AI</div>
                  <div className="text-xs text-slate-400">فرز وتلخيص تلقائي</div>
                </div>
                <div className="p-3.5 rounded-2xl bg-[#111728] border border-slate-800 text-center">
                  <div className="text-lg font-bold text-amber-400 mb-0.5">سحابي</div>
                  <div className="text-xs text-slate-400">مزامنة فورية متعددة</div>
                </div>
              </div>

              {/* Recent Conversations with direct click */}
              <div className="space-y-2">
                <h4 className="text-xs font-bold text-slate-400">المحادثات النشطة حالياً</h4>
                <div className="divide-y divide-slate-800/60 rounded-2xl border border-slate-800 overflow-hidden bg-[#0a0f1c]">
                  {conversations.slice(0, 5).map((c, idx) => (
                    <div
                      key={`${c.id}_${idx}`}
                      onClick={() => onOpenChat(c.id)}
                      className="p-3.5 flex items-center justify-between hover:bg-[#121929] cursor-pointer transition"
                    >
                      <div className="flex items-center gap-3">
                        <img
                          src={c.avatar}
                          alt=""
                          className="w-10 h-10 rounded-2xl object-cover border border-slate-700"
                        />
                        <div>
                          <div className="text-xs font-bold text-white flex items-center gap-1.5">
                            <span>{c.title}</span>
                            {c.isPinned && <span className="text-[10px] text-amber-400">📌</span>}
                          </div>
                          <div className="text-[11px] text-slate-400 truncate max-w-xs">
                            {c.lastMessage?.text || 'لا توجد رسائل سابقة'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button className="px-3 py-1 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-xs font-bold border border-emerald-500/30">
                          فتح الدردشة
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 2. MODULE: AI SUPER ENGINE */}
        {activeModule === 'ai' && (
          <div className="space-y-6">
            <div className="rounded-3xl bg-[#0d1322] border border-indigo-500/30 p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                  <h3 className="text-base font-bold text-white flex items-center gap-2">
                    <Bot className="w-5 h-5 text-indigo-400" />
                    <span>وتين AI الخارق — كاتب الرسائل والمساعد الشخصي</span>
                  </h3>
                  <p className="text-xs text-slate-400">
                    كتابة رسائل بالنيابة، اقتراح الردود، تلخيص المحادثات، تحويل الصوت والمستندات، وشخصيات متعددة.
                  </p>
                </div>
                <button
                  onClick={onOpenAIModal}
                  className="px-4 py-2 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold flex items-center gap-1.5 shadow-lg shadow-indigo-500/20"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>المساعد التفاعلي الشامل</span>
                </button>
              </div>

              {/* Interactive Quick Drafter */}
              <div className="rounded-2xl bg-[#101626] border border-slate-800 p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-300">أداة صياغة الرسائل السريعة بالذكاء الاصطناعي:</span>
                  <div className="flex items-center gap-1.5 overflow-x-auto">
                    {(['formal', 'casual', 'polite', 'promo', 'apology'] as const).map((t) => (
                      <button
                        key={t}
                        onClick={() => setAiQuickTone(t)}
                        className={`px-2.5 py-1 rounded-xl text-[11px] font-bold transition ${
                          aiQuickTone === t
                            ? 'bg-indigo-500 text-white shadow-sm'
                            : 'bg-slate-800 text-slate-400 hover:text-white'
                        }`}
                      >
                        {t === 'formal'
                          ? 'رسمي 👔'
                          : t === 'casual'
                          ? 'عفوي ☕'
                          : t === 'polite'
                          ? 'مهذب ✨'
                          : t === 'promo'
                          ? 'تسويقي 📢'
                          : 'اعتذار 🙏'}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="اكتب فكرة الرسالة (مثال: طلب تمديد تسليم التقرير لغد، أو تهنئة بالمولود الجديد)..."
                    value={aiQuickInput}
                    onChange={(e) => setAiQuickInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleGenerateQuickAI()}
                    className="flex-1 bg-[#090d16] border border-slate-800 rounded-xl px-3.5 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500/50"
                  />
                  <button
                    onClick={handleGenerateQuickAI}
                    disabled={aiQuickLoading || !aiQuickInput.trim()}
                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-400 hover:to-purple-500 disabled:opacity-50 text-white text-xs font-bold transition flex items-center gap-1.5"
                  >
                    {aiQuickLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                    <span>صياغة بالنيابة</span>
                  </button>
                </div>

                {aiQuickResult && (
                  <div className="p-4 rounded-xl bg-indigo-950/30 border border-indigo-500/30 text-xs text-slate-200 leading-relaxed relative group">
                    <p className="whitespace-pre-wrap">{aiQuickResult}</p>
                    <div className="mt-3 flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleCopy(aiQuickResult)}
                        className="px-3 py-1 rounded-lg bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-300 text-xs flex items-center gap-1 border border-indigo-500/30"
                      >
                        {copiedText === aiQuickResult ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                        <span>نسخ النص</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* 50 AI Feature Matrix Highlights */}
              <div className="mt-6 pt-5 border-t border-slate-800">
                <h4 className="text-xs font-bold text-slate-300 mb-3">حزم الذكاء الاصطناعي الجاهزة في وتين:</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="p-3.5 rounded-2xl bg-[#0a0f1c] border border-slate-800/80">
                    <div className="text-xs font-bold text-indigo-400 mb-1">📄 تحليل المستندات وOCR</div>
                    <p className="text-[11px] text-slate-400 leading-relaxed">
                      تحليل ملفات PDF، استخراج النصوص من الصور، وتلخيص المستندات الكبيرة واستخراج أهم النقاط.
                    </p>
                  </div>
                  <div className="p-3.5 rounded-2xl bg-[#0a0f1c] border border-slate-800/80">
                    <div className="text-xs font-bold text-emerald-400 mb-1">🎙️ الصوت الذكي والتسجيلات</div>
                    <p className="text-[11px] text-slate-400 leading-relaxed">
                      تحويل الصوت إلى نصوص، تلخيص التسجيلات الصوتية، والبحث المتقدم داخل محتوى الصوت.
                    </p>
                  </div>
                  <div className="p-3.5 rounded-2xl bg-[#0a0f1c] border border-slate-800/80">
                    <div className="text-xs font-bold text-amber-400 mb-1">🧠 الذاكرة والمساعد الشخصي</div>
                    <p className="text-[11px] text-slate-400 leading-relaxed">
                      شخصيات مخصصة (دراسة، عمل، سفر، برمجة)، تلخيص ما فاتك، واقتراح الردود الذكية حسب سياقك.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 3. MODULE: CALLS & AUDIO ROOMS */}
        {activeModule === 'calls' && (
          <div className="space-y-6">
            <div className="rounded-3xl bg-[#0d1322] border border-sky-500/30 p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                  <h3 className="text-base font-bold text-white flex items-center gap-2">
                    <Phone className="w-5 h-5 text-sky-400" />
                    <span>المكالمات والغرف الصوتية المباشرة (Voice & Video Rooms)</span>
                  </h3>
                  <p className="text-xs text-slate-400">
                    مكالمات HD، غرف صوتية مفتوحة، مشاركة الشاشة، عزل الضوضاء، والترجمة المباشرة للكلام.
                  </p>
                </div>
                <button
                  onClick={onOpenStartCall}
                  className="px-4 py-2 rounded-2xl bg-sky-500 hover:bg-sky-400 text-white text-xs font-bold flex items-center gap-1.5 shadow-lg shadow-sky-500/20"
                >
                  <Phone className="w-3.5 h-3.5" />
                  <span>بدء مكالمة جديدة</span>
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 rounded-2xl bg-[#111728] border border-slate-800">
                  <div className="flex items-center gap-2 text-sky-400 text-xs font-bold mb-2">
                    <Mic className="w-4 h-4" />
                    <span>غرفة صوتية عامة: رواد تقنية وتين 🎙️</span>
                  </div>
                  <p className="text-xs text-slate-400 mb-3">
                    مساحة نقاش حية حول الذكاء الاصطناعي ومستقبل التطبيقات الفائقة. 18 مشاركاً نشطاً الآن.
                  </p>
                  <button
                    onClick={onOpenStartCall}
                    className="w-full py-2 rounded-xl bg-sky-500/20 hover:bg-sky-500/30 text-sky-300 text-xs font-bold border border-sky-500/30 transition"
                  >
                    الانضمام للغرفة الصوتية
                  </button>
                </div>

                <div className="p-4 rounded-2xl bg-[#111728] border border-slate-800">
                  <div className="flex items-center gap-2 text-emerald-400 text-xs font-bold mb-2">
                    <Video className="w-4 h-4" />
                    <span>مكالمات الفيديو الفائقة ومشاركة الشاشة</span>
                  </div>
                  <p className="text-xs text-slate-400 mb-3">
                    مكالمات مشفرة بدون حد أقصى للوقت، ميزة عزل الضوضاء بالـ AI وخلفيات افتراضية مدمجة.
                  </p>
                  <button
                    onClick={onOpenStartCall}
                    className="w-full py-2 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 text-xs font-bold border border-emerald-500/30 transition"
                  >
                    تجربة الاتصال المباشر
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 6. MODULE: WATEEN BUSINESS */}
        {activeModule === 'business' && (
          <div className="space-y-6">
            <div className="rounded-3xl bg-[#0d1322] border border-amber-500/30 p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                  <h3 className="text-base font-bold text-white flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-amber-400" />
                    <span>وتين للأعمال (Wateen Business Suite & CRM)</span>
                  </h3>
                  <p className="text-xs text-slate-400">
                    حساب تجاري معتمد، كتالوج المنتجات والأسعار، ردود تلقائية AI، وإدارة العملاء واستقبال الطلبات.
                  </p>
                </div>
                <button
                  onClick={onOpenPremium}
                  className="px-4 py-2 rounded-2xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs shadow-lg shadow-amber-500/20"
                >
                  ترقية إلى Business Pro
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                <div className="p-4 rounded-2xl bg-[#111728] border border-slate-800">
                  <div className="text-xs text-slate-400 mb-1">الردود الذكية للعملاء</div>
                  <div className="text-lg font-bold text-white">99.4% جاهزية</div>
                  <div className="text-[11px] text-emerald-400 mt-1">رد آلي بالـ AI خلال ثوانٍ</div>
                </div>
                <div className="p-4 rounded-2xl bg-[#111728] border border-slate-800">
                  <div className="text-xs text-slate-400 mb-1">طلبات العملاء النشطة</div>
                  <div className="text-lg font-bold text-amber-400">14 طلباً جديداً</div>
                  <div className="text-[11px] text-slate-400 mt-1">توزيع آلي على الموظفين</div>
                </div>
                <div className="p-4 rounded-2xl bg-[#111728] border border-slate-800">
                  <div className="text-xs text-slate-400 mb-1">رمز QR المباشر للشركة</div>
                  <div className="text-lg font-bold text-indigo-400">جاهز للمشاركة</div>
                  <div className="text-[11px] text-slate-400 mt-1">رابط مباشر للدردشة الفورية</div>
                </div>
              </div>

              <div className="p-4 rounded-2xl bg-[#101626] border border-slate-800">
                <h4 className="text-xs font-bold text-white mb-2">رسائل خارج أوقات العمل والردود التلقائية:</h4>
                <p className="text-xs text-slate-300 leading-relaxed">
                  "أهلاً بك في وتين للأعمال! نحن حالياً خارج أوقات العمل الرسمية، ولكن مساعدنا الذكي يسجل طلبك وسنتواصل معك فوراً في تمام الساعة 9:00 صباحاً."
                </p>
              </div>
            </div>
          </div>
        )}

        {/* 7. MODULE: WATEEN STORE */}
        {activeModule === 'store' && (
          <div className="space-y-6">
            <div className="rounded-3xl bg-[#0d1322] border border-orange-500/30 p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                  <h3 className="text-base font-bold text-white flex items-center gap-2">
                    <ShoppingBag className="w-5 h-5 text-orange-400" />
                    <span>سوق وتين الرقمي المدمج (Marketplace)</span>
                  </h3>
                  <p className="text-xs text-slate-400">
                    تصفح وشراء المنتجات، سلة تسوق، تتبع الشحنات، متاجر موثقة، وتوصيات مدعومة بالذكاء الاصطناعي.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="px-3 py-1.5 rounded-xl bg-orange-500/10 border border-orange-500/30 text-orange-300 text-xs font-bold flex items-center gap-1.5">
                    <ShoppingCart className="w-3.5 h-3.5" />
                    <span>السلة ({cartCount})</span>
                  </div>
                </div>
              </div>

              {/* Product cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {storeItems.map((item) => (
                  <div
                    key={item.id}
                    className="rounded-2xl bg-[#111728] border border-slate-800 overflow-hidden flex flex-col justify-between hover:border-orange-500/40 transition"
                  >
                    <div className="relative h-40 overflow-hidden bg-slate-900">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                      <span className="absolute top-2.5 right-2.5 px-2 py-0.5 rounded-full bg-slate-950/80 backdrop-blur-md text-amber-300 text-[10px] font-bold border border-amber-500/30">
                        {item.badge}
                      </span>
                    </div>

                    <div className="p-4 flex-1 flex flex-col justify-between">
                      <div>
                        <div className="text-[11px] text-slate-400 mb-1">{item.category}</div>
                        <h4 className="text-xs font-bold text-white mb-2 line-clamp-2">{item.name}</h4>
                        <div className="flex items-center justify-between text-xs mb-3">
                          <span className="text-orange-400 font-bold">{item.price}</span>
                          <span className="text-slate-400 text-[11px]">⭐ {item.rating}</span>
                        </div>
                      </div>

                      <button
                        onClick={() => {
                          setCartCount(cartCount + 1);
                          soundEffects.playPop();
                        }}
                        className="w-full py-2 rounded-xl bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-400 hover:to-amber-500 text-white font-bold text-xs shadow-md shadow-orange-500/20 active:scale-95 transition"
                      >
                        إضافة إلى السلة 🛒
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 10. MODULE: TASKS & PRODUCTIVITY */}
        {activeModule === 'tasks' && (
          <div className="space-y-6">
            <div className="rounded-3xl bg-[#0d1322] border border-lime-500/30 p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                  <h3 className="text-base font-bold text-white flex items-center gap-2">
                    <CheckSquare className="w-5 h-5 text-lime-400" />
                    <span>المهام والإنتاجية الذكية (Tasks & To-Do)</span>
                  </h3>
                  <p className="text-xs text-slate-400">
                    أنشئ قوائم مهام، حوّل أي رسالة في المحادثة إلى مهمة بنقرة زر، وتتبع إنجاز أهدافك اليومية.
                  </p>
                </div>
              </div>

              {/* Add Task Input */}
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  placeholder="أضف مهمة جديدة أو تذكيراً..."
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddTask()}
                  className="flex-1 bg-[#101626] border border-slate-800 rounded-xl px-3.5 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-lime-500/50"
                />
                <button
                  onClick={handleAddTask}
                  className="px-4 py-2 rounded-xl bg-lime-500 hover:bg-lime-400 text-slate-950 font-bold text-xs transition"
                >
                  إضافة مهمة
                </button>
              </div>

              {/* Tasks List */}
              <div className="space-y-2">
                {tasks.map((task) => (
                  <div
                    key={task.id}
                    onClick={() => toggleTask(task.id)}
                    className={`p-3.5 rounded-2xl border cursor-pointer flex items-center justify-between transition ${
                      task.done
                        ? 'bg-[#090d16]/60 border-slate-800/50 text-slate-500'
                        : 'bg-[#111728] border-slate-800 text-slate-200 hover:border-lime-500/40'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-5 h-5 rounded-lg border flex items-center justify-center transition ${
                          task.done ? 'bg-lime-500 border-lime-500 text-slate-950' : 'border-slate-700 bg-slate-900'
                        }`}
                      >
                        {task.done && <Check className="w-3.5 h-3.5 font-black" />}
                      </div>
                      <span className={`text-xs ${task.done ? 'line-through text-slate-500' : 'font-medium'}`}>
                        {task.title}
                      </span>
                    </div>
                    <span className="text-[11px] text-slate-500">{task.time}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 9. MODULE: CALENDAR */}
        {activeModule === 'calendar' && (
          <div className="space-y-6">
            <div className="rounded-3xl bg-[#0d1322] border border-emerald-500/30 p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                  <h3 className="text-base font-bold text-white flex items-center gap-2">
                    <CalendarIcon className="w-5 h-5 text-emerald-400" />
                    <span>التقويم الذكي والمواعيد المستخرجة من المحادثات</span>
                  </h3>
                  <p className="text-xs text-slate-400">
                    يكتشف الذكاء الاصطناعي المواعيد المذكورة في الدردشات ويضيفها لجدولك تلقائياً بعد موافقتك.
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                {calendarEvents.map((ev) => (
                  <div
                    key={ev.id}
                    className="p-4 rounded-2xl bg-[#111728] border border-slate-800 flex items-center justify-between"
                  >
                    <div>
                      <div className="text-xs font-bold text-white mb-1">{ev.title}</div>
                      <div className="text-[11px] text-emerald-400 flex items-center gap-1.5">
                        <Clock className="w-3 h-3" />
                        <span>{ev.date} — {ev.time}</span>
                      </div>
                    </div>
                    <span className="px-3 py-1 rounded-xl bg-slate-800 text-slate-300 text-xs">
                      مجدول تلقائياً ✨
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 13. MODULE: PRIVACY & SECURITY */}
        {activeModule === 'privacy' && (
          <div className="space-y-6">
            <div className="rounded-3xl bg-[#0d1322] border border-rose-500/30 p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                  <h3 className="text-base font-bold text-white flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-rose-400" />
                    <span>مركز الأمان والخصوصية الفائقة</span>
                  </h3>
                  <p className="text-xs text-slate-400">
                    قفل التطبيق (PIN / Face ID)، إدارة الجلسات والأجهزة، كاشف الروابط المشبوهة، والرسائل المؤقتة.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 rounded-2xl bg-[#111728] border border-slate-800">
                  <div className="text-xs font-bold text-white mb-1">🔐 قفل وتين بالبصمة ورمز PIN</div>
                  <p className="text-[11px] text-slate-400 mb-3">حماية فورية عند الخروج من التطبيق لمنع الوصول غير المصرح به.</p>
                  <span className="inline-block px-3 py-1 rounded-xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 text-xs font-bold">
                    مفعل وآمن ✅
                  </span>
                </div>

                <div className="p-4 rounded-2xl bg-[#111728] border border-slate-800">
                  <div className="text-xs font-bold text-white mb-1">🛡️ كاشف الروابط الخبيثة ومكافحة التصيد</div>
                  <p className="text-[11px] text-slate-400 mb-3">فحص فوري تلقائي لأي رابط يرسل في المحادثات وتنبيهك قبل فتحه.</p>
                  <span className="inline-block px-3 py-1 rounded-xl bg-indigo-500/10 text-indigo-400 border border-indigo-500/30 text-xs font-bold">
                    حماية استباقية بالذكاء الاصطناعي 🤖
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 11. MODULE: LOCATION */}
        {activeModule === 'location' && (
          <div className="space-y-6">
            <div className="rounded-3xl bg-[#0d1322] border border-teal-500/30 p-5 sm:p-6">
              <h3 className="text-base font-bold text-white flex items-center gap-2 mb-2">
                <MapPin className="w-5 h-5 text-teal-400" />
                <span>مشاركة الموقع الحي ونقاط اللقاء (Live Location)</span>
              </h3>
              <p className="text-xs text-slate-400 mb-6">
                شارك موقعك الحالي أو موقعك الحي المباشر مع أصدقائك أو مندوب التوصيل بأمان مع تحديد مدة المشاركة.
              </p>

              <div className="p-4 rounded-2xl bg-[#111728] border border-slate-800 text-center">
                <div className="w-12 h-12 rounded-2xl bg-teal-500/10 border border-teal-500/30 text-teal-400 flex items-center justify-center mx-auto mb-3">
                  <MapPin className="w-6 h-6" />
                </div>
                <div className="text-xs font-bold text-white mb-1">مشاركة الإحداثيات الحالية</div>
                <p className="text-[11px] text-slate-400 max-w-sm mx-auto mb-4">
                  يمكنك إرسال إحداثيات موقعك في أي محادثة فورية أو تتبع المسار في الوقت الفعلي.
                </p>
                <button
                  onClick={() => {
                    soundEffects.playSent();
                    alert('تم نسخ إحداثيات الموقع الحالي لمشاركتها في الدردشة!');
                  }}
                  className="px-4 py-2 rounded-xl bg-teal-500 hover:bg-teal-400 text-slate-950 font-bold text-xs shadow-md shadow-teal-500/20"
                >
                  نسخ إحداثيات موقعي الحالية 📍
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 12. MODULE: MEDIA STUDIO */}
        {activeModule === 'media' && (
          <div className="space-y-6">
            <div className="rounded-3xl bg-[#0d1322] border border-fuchsia-500/30 p-5 sm:p-6">
              <h3 className="text-base font-bold text-white flex items-center gap-2 mb-2">
                <Music className="w-5 h-5 text-fuchsia-400" />
                <span>استوديو وتين للوسائط والصور والفيديو</span>
              </h3>
              <p className="text-xs text-slate-400 mb-6">
                تحسين جودة الصور بالذكاء الاصطناعي، إزالة الخلفيات، قص الفيديوهات، واستخراج الصوت تلقائياً.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="p-4 rounded-2xl bg-[#111728] border border-slate-800">
                  <div className="text-xs font-bold text-fuchsia-400 mb-1">🎨 محرر الصور الذكي</div>
                  <p className="text-[11px] text-slate-400">إزالة الخلفية بضغطة زر، فلاتر، ملصقات، وصانع Avatar مميز.</p>
                </div>
                <div className="p-4 rounded-2xl bg-[#111728] border border-slate-800">
                  <div className="text-xs font-bold text-pink-400 mb-1">🎬 محرر الفيديو والكابشن</div>
                  <p className="text-[11px] text-slate-400">توليد ترجمة نصية تلقائية للفيديو (Captions) وتلخيص المقاطع.</p>
                </div>
                <div className="p-4 rounded-2xl bg-[#111728] border border-slate-800">
                  <div className="text-xs font-bold text-violet-400 mb-1">🔊 تحويل النصوص إلى صوت</div>
                  <p className="text-[11px] text-slate-400">استمع لأي رسالة نصية أو مقال بصوت طبيعي عالي النقاء.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 8. MODULE: FILES & VAULT */}
        {activeModule === 'files' && (
          <div className="space-y-6">
            <div className="rounded-3xl bg-[#0d1322] border border-cyan-500/30 p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-base font-bold text-white flex items-center gap-2">
                    <FolderLock className="w-5 h-5 text-cyan-400" />
                    <span>خزنة وتين السحابية والكبسولة الزمنية</span>
                  </h3>
                  <p className="text-xs text-slate-400">مستندات مشفرة، ملفات مشتركة، وكبسولات رسائل تفتح في تاريخ مستقبلي محدد.</p>
                </div>
                <button
                  onClick={() => onNavigateTab('vault')}
                  className="px-4 py-2 rounded-2xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-xs"
                >
                  فتح الخزنة الرئيسية
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 4. MODULE: COMMUNITIES */}
        {activeModule === 'communities' && (
          <div className="space-y-6">
            <div className="rounded-3xl bg-[#0d1322] border border-violet-500/30 p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-base font-bold text-white flex items-center gap-2">
                    <Users className="w-5 h-5 text-violet-400" />
                    <span>المجتمعات والمجموعات الكبرى</span>
                  </h3>
                  <p className="text-xs text-slate-400">مجموعات عامة وخاصة، استطلاعات رأي، ومساعد AI لتنظيم المحادثات الجماعية.</p>
                </div>
                <button
                  onClick={onOpenCreateChat}
                  className="px-4 py-2 rounded-2xl bg-violet-600 hover:bg-violet-500 text-white font-bold text-xs"
                >
                  إنشاء مجتمع جديد
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 5. MODULE: CHANNELS */}
        {activeModule === 'channels' && (
          <div className="space-y-6">
            <div className="rounded-3xl bg-[#0d1322] border border-pink-500/30 p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-base font-bold text-white flex items-center gap-2">
                    <Radio className="w-5 h-5 text-pink-400" />
                    <span>القنوات الرسمية والبث العام</span>
                  </h3>
                  <p className="text-xs text-slate-400">تابع أحدث الأخبار، الإعلانات الرسمية، وبثوث وتين الحصرية.</p>
                </div>
                <button
                  onClick={() => onNavigateTab('discover')}
                  className="px-4 py-2 rounded-2xl bg-pink-600 hover:bg-pink-500 text-white font-bold text-xs"
                >
                  استكشاف القنوات
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
